import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import '../styles/SearchSection.css';
import CoverImage from './CoverImage';
import {
  addToReadingList,
  clearReadingList,
  downloadTextFile,
  generateBibtex,
  generateRis,
  isInReadingList,
  loadReadingList,
  removeFromReadingList,
} from '../services/readingList';
import { trackEvent } from '../services/analytics';

const RECENT_SEARCHES_KEY = 'opac_recent_searches_v1';

const tokenizeForHighlight = (value = '') => value
  .toLowerCase()
  .split(/[^a-z0-9]+/)
  .map((token) => token.trim())
  .filter((token) => token.length >= 2)
  .slice(0, 8);

const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const ITEM_TYPE_LABELS = {
  AN: 'Author Nook',
  BIOGRAPHIE: 'Autobiographies / Biographies',
  BK: 'Books',
  BKC: 'Books (Customized)',
  BV: 'Bound Volumes / Journals',
  C: "Children's Collection",
  CD: 'Non-Book Materials (CD/DVD/Maps)',
  CM: 'Cartographic Material',
  CP: 'Conference / Proceedings',
  CPDA: 'Longterm Books',
  CR: 'Course Reserve',
  DEB: 'Subscription e-Book',
  DT: 'Demanded Titles',
  E: 'Competitive Exam Collection',
  EB: 'e-Book',
  EBK: 'e-Books',
  FB: 'IITH Faculty Authored Books',
  FC: 'Fiction Collection',
  HF01: 'Hall of Fame',
  JPC: 'Japanese Books Collection',
  'LOST BOOK': 'Lost Book',
  MIT: 'MIT – Movers, Innovators, Thinkers',
  MOOC: 'Massive Open Online Courses',
  PR: 'Project Report',
  QRS: 'Quick Reference Section',
  RAJBHASHA: 'Rajbhasha Hindi Books',
  RC: 'Reference Collection',
  TB: 'Textbook',
  TELGU: 'Telugu Book Collection',
  TH: 'Thesis',
  WC: 'Wheel Chair',
  X1: 'Course Material',
  XM: 'Loose-leaf / Xerox Material',
};

const itemTypeLabel = (code) => ITEM_TYPE_LABELS[code] || code;

const buildLocalFacets = (rows = []) => {
  const availability = { available: 0, unavailable: 0 };
  const itemTypes = {};
  const years = {};

  rows.forEach((row) => {
    if ((Number(row.available_count) || 0) > 0) {
      availability.available += 1;
    } else {
      availability.unavailable += 1;
    }

    String(row.item_types || row.itemtype || '')
      .split(',')
      .map((token) => token.trim())
      .filter(Boolean)
      .forEach((token) => {
        itemTypes[token] = (itemTypes[token] || 0) + 1;
      });

    const year = String(row.copyrightdate || '').trim();
    if (/^\d{4}$/.test(year)) {
      years[year] = (years[year] || 0) + 1;
    }
  });

  return {
    availability,
    itemTypes,
    years,
  };
};

function SearchSection() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('title');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [readingList, setReadingList] = useState([]);
  const [didYouMean, setDidYouMean] = useState('');
  const [autoCorrection, setAutoCorrection] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [previewResults, setPreviewResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const [viewMode, setViewMode] = useState('list');
  const [facets, setFacets] = useState({ availability: {}, itemTypes: {}, years: {} });
  const [facetFilters, setFacetFilters] = useState({ availability: 'all', itemType: 'all', year: 'all' });
  const [popularSearches, setPopularSearches] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const resultsRef = useRef(null);
  const firstResultRef = useRef(null);
  const pendingResultScrollRef = useRef(false);
  const suggestionTimerRef = useRef(null);
  const suggestionAbortRef = useRef(null);
  const suggestionsCacheRef = useRef(new Map());
  const inputWrapRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);
  const previewAbortRef = useRef(null);
  const previewCacheRef = useRef(new Map());
  const searchAbortRef = useRef(null);
  const searchRequestSeqRef = useRef(0);
  const lastSearchRef = useRef({ query: '', at: 0 });

  const scrollResultIntoView = (target) => {
    if (!target) {
      return;
    }

    const header = document.querySelector('.institute-header');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const extraGap = 12;
    const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - extraGap;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  useEffect(() => {
    try {
      const savedSearch = localStorage.getItem('searchState');
      if (savedSearch) {
        const { query, type, results: savedResults, pagination: savedPagination } = JSON.parse(savedSearch);
        setSearchQuery(query || '');
        setSearchType(type || 'title');
        setResults(Array.isArray(savedResults) ? savedResults : []);
        setPagination(savedPagination || {});

        const savedScroll = localStorage.getItem('searchScrollPosition');
        if (savedScroll) {
          setTimeout(() => {
            window.scrollTo(0, parseInt(savedScroll, 10) || 0);
          }, 100);
        }

        localStorage.removeItem('searchState');
        localStorage.removeItem('searchScrollPosition');
      }
    } catch (_error) {
      // Ignore malformed or inaccessible localStorage state.
    }

    setReadingList(loadReadingList());

    try {
      const savedRecentSearches = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
      if (Array.isArray(savedRecentSearches)) {
        setRecentSearches(savedRecentSearches.slice(0, 8));
      }
    } catch (_error) {
      // Ignore malformed payload.
    }

    return () => {
      if (suggestionTimerRef.current) {
        clearTimeout(suggestionTimerRef.current);
      }
      if (suggestionAbortRef.current) {
        suggestionAbortRef.current.abort();
      }
      if (previewAbortRef.current) {
        previewAbortRef.current.abort();
      }
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    fetch(`/api/search/meta?type=${encodeURIComponent(searchType)}&limit=8`)
      .then((response) => (response.ok ? response.json() : { popular: [] }))
      .then((data) => {
        setPopularSearches(Array.isArray(data.popular) ? data.popular : []);
      })
      .catch(() => {
        setPopularSearches([]);
      });
  }, [searchType]);

  useEffect(() => {
    if (!pendingResultScrollRef.current || !results.length) {
      return;
    }

    pendingResultScrollRef.current = false;
    scrollResultIntoView(firstResultRef.current || resultsRef.current);
  }, [results, pagination.currentPage]);

  const highlightedTokens = useMemo(() => tokenizeForHighlight(searchQuery), [searchQuery]);
  const isDropdownOpen = showSuggestions && (previewResults.length > 0 || suggestions.length > 0);

  // Flat list mirroring the dropdown's render order (preview cards first, then text suggestions)
  // so arrow-key navigation can walk across both groups with a single index.
  const navigableOptions = useMemo(() => ([
    ...previewResults.map((book) => ({ type: 'preview', book })),
    ...suggestions.map((suggestion) => ({ type: 'suggestion', suggestion })),
  ]), [previewResults, suggestions]);

  useEffect(() => {
    setActiveOptionIndex(-1);
  }, [previewResults, suggestions, showSuggestions]);

  // The dropdown is rendered via a portal into document.body (see render below) so that no
  // ancestor's overflow/height can ever clip it — it's positioned using the input's real
  // on-screen coordinates instead of relying on CSS absolute-positioning within the page flow.
  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }

    const updatePosition = () => {
      if (!inputWrapRef.current) {
        return;
      }
      const rect = inputWrapRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isDropdownOpen]);

  const filteredResults = useMemo(() => results.filter((book) => {
    if (facetFilters.availability === 'available' && Number(book.available_count || 0) <= 0) {
      return false;
    }
    if (facetFilters.availability === 'unavailable' && Number(book.available_count || 0) > 0) {
      return false;
    }

    if (facetFilters.itemType !== 'all') {
      const itemTypes = String(book.item_types || '')
        .split(',')
        .map((itemType) => itemType.trim())
        .filter(Boolean);
      if (!itemTypes.includes(facetFilters.itemType)) {
        return false;
      }
    }

    if (facetFilters.year !== 'all' && String(book.copyrightdate || '') !== facetFilters.year) {
      return false;
    }

    return true;
  }), [facetFilters, results]);

  // Live facet counts that react to cross-filter selections.
  // Each facet's counts are computed from results that pass all *other* active filters,
  // so selections in one dropdown correctly narrow the options of the others.
  const liveFacets = useMemo(() => {
    const availability = { available: 0, unavailable: 0 };
    const itemTypes = {};
    const years = {};

    results.forEach((book) => {
      const bookAvail = Number(book.available_count || 0) > 0;
      const bookYear = String(book.copyrightdate || '').trim();
      const bookTypes = String(book.item_types || book.itemtype || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const passesAvail = facetFilters.availability === 'all'
        || (facetFilters.availability === 'available' && bookAvail)
        || (facetFilters.availability === 'unavailable' && !bookAvail);
      const passesItemType = facetFilters.itemType === 'all' || bookTypes.includes(facetFilters.itemType);
      const passesYear = facetFilters.year === 'all' || bookYear === facetFilters.year;

      // Availability counts: apply itemType + year filters only
      if (passesItemType && passesYear) {
        if (bookAvail) availability.available += 1;
        else availability.unavailable += 1;
      }

      // Item-type counts: apply availability + year filters only
      if (passesAvail && passesYear) {
        bookTypes.forEach((t) => { itemTypes[t] = (itemTypes[t] || 0) + 1; });
      }

      // Year counts: apply availability + itemType filters only
      if (passesAvail && passesItemType && /^\d{4}$/.test(bookYear)) {
        years[bookYear] = (years[bookYear] || 0) + 1;
      }
    });

    return { availability, itemTypes, years };
  }, [results, facetFilters]);

  const searchTypes = [
    { value: 'title', label: 'Title' },
    { value: 'author', label: 'Author' },
    { value: 'subject', label: 'Subject' },
    { value: 'callnumber', label: 'Call Number' },
    { value: 'isbn', label: 'ISBN' },
    { value: 'bc', label: 'Accession Number' },
  ];

  const persistRecentSearch = (query, type) => {
    const normalized = String(query || '').trim();
    if (!normalized) {
      return;
    }

    const nextRecent = [
      { query: normalized, type, at: new Date().toISOString() },
      ...recentSearches.filter((item) => !(item.query.toLowerCase() === normalized.toLowerCase() && item.type === type)),
    ].slice(0, 8);

    setRecentSearches(nextRecent);
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(nextRecent));
    } catch (_error) {
      // Ignore storage failures.
    }
  };

  const fetchSuggestions = async (value) => {
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      return;
    }

    const cacheKey = `${searchType}::${trimmed.toLowerCase()}`;
    const cachedSuggestions = suggestionsCacheRef.current.get(cacheKey);
    if (cachedSuggestions) {
      setSuggestions(cachedSuggestions);
      return;
    }

    if (suggestionAbortRef.current) {
      suggestionAbortRef.current.abort();
    }

    const controller = new AbortController();
    suggestionAbortRef.current = controller;

    try {
      const response = await fetch(
        `/api/search/suggest?q=${encodeURIComponent(trimmed)}&type=${encodeURIComponent(searchType)}&limit=8`,
        { signal: controller.signal },
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const nextSuggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
      suggestionsCacheRef.current.set(cacheKey, nextSuggestions);
      if (suggestionsCacheRef.current.size > 120) {
        const oldestKey = suggestionsCacheRef.current.keys().next().value;
        if (oldestKey) {
          suggestionsCacheRef.current.delete(oldestKey);
        }
      }
      setSuggestions(nextSuggestions);
    } catch (fetchError) {
      if (fetchError.name !== 'AbortError') {
        setSuggestions([]);
      }
    }
  };

  const fetchPreview = async (value) => {
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setPreviewResults([]);
      return;
    }

    const cacheKey = `${searchType}::${trimmed.toLowerCase()}`;
    const cachedPreview = previewCacheRef.current.get(cacheKey);
    if (cachedPreview) {
      setPreviewResults(cachedPreview);
      return;
    }

    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
    }

    const controller = new AbortController();
    previewAbortRef.current = controller;

    try {
      const response = await fetch(
        `/api/search/preview?q=${encodeURIComponent(trimmed)}&type=${encodeURIComponent(searchType)}&limit=5`,
        { signal: controller.signal },
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const nextPreview = Array.isArray(data.books) ? data.books : [];
      previewCacheRef.current.set(cacheKey, nextPreview);
      if (previewCacheRef.current.size > 120) {
        const oldestKey = previewCacheRef.current.keys().next().value;
        if (oldestKey) {
          previewCacheRef.current.delete(oldestKey);
        }
      }
      setPreviewResults(nextPreview);
    } catch (fetchError) {
      if (fetchError.name !== 'AbortError') {
        setPreviewResults([]);
      }
    }
  };

  const handleInputChange = (value) => {
    setSearchQuery(value);
    setDidYouMean('');
    setError(null);
    setAutoCorrection(null);
    setShowSuggestions(Boolean(value.trim()));

    if (suggestionTimerRef.current) {
      clearTimeout(suggestionTimerRef.current);
    }

    suggestionTimerRef.current = setTimeout(() => {
      fetchSuggestions(value);
      fetchPreview(value);
    }, 180);
  };

  const performSearch = async (queryText, offset = 0) => {
    searchRequestSeqRef.current += 1;
    const requestSeq = searchRequestSeqRef.current;

    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setLoading(true);
    setError(null);
    setDidYouMean('');
    setAutoCorrection(null);

    try {
      const response = await fetch('/api/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: queryText,
          type: searchType,
          limit: 30,
          offset,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      if (requestSeq !== searchRequestSeqRef.current) {
        return;
      }

      const nextResults = data.books || [];
      setResults(nextResults);
      setFacets(data.facets || buildLocalFacets(nextResults));

      if (offset === 0) {
        setFacetFilters({ availability: 'all', itemType: 'all', year: 'all' });
      }

      if (data.autoApplied && data.didYouMean) {
        setAutoCorrection({ from: data.originalQuery || queryText, to: data.didYouMean });
        setSearchQuery(data.didYouMean);
        trackEvent('search_autocorrected', {
          from: String(data.originalQuery || queryText).slice(0, 120),
          to: String(data.didYouMean).slice(0, 120),
          searchType,
        });
      }

      const currentPage = Math.floor(offset / 30) + 1;
      const totalPages = Math.ceil((data.total || 0) / 30);

      setPagination({
        offset,
        hasMore: nextResults.length === 30 && offset + 30 < data.total,
        canGoBack: offset > 0,
        currentPage,
        totalPages,
        total: data.total,
      });

      if (offset === 0) {
        persistRecentSearch(queryText, searchType);
      }

      if (!nextResults.length) {
        const suggestion = String(data.didYouMean || '').trim();
        setDidYouMean(suggestion);
        setError('No results found');
        if (offset === 0) {
          trackEvent('search_zero_results', {
            searchType,
            query: queryText.slice(0, 120),
            queryLength: queryText.length,
            didYouMean: suggestion || undefined,
          });
        }
      }
    } catch (err) {
      if (err.name === 'AbortError' || requestSeq !== searchRequestSeqRef.current) {
        return;
      }
      setError('Error searching. Please try again.');
      console.error('Search error:', err);
    } finally {
      if (requestSeq === searchRequestSeqRef.current) {
        setLoading(false);
      }
    }
  };

  const submitSearch = async (queryText, offset = 0, source = 'manual') => {
    const trimmedQuery = String(queryText || '').trim();
    if (!trimmedQuery) {
      setError('Please enter a search term');
      return;
    }

    if (offset === 0) {
      const now = Date.now();
      const previous = lastSearchRef.current;
      if (previous.query && previous.query.toLowerCase() !== trimmedQuery.toLowerCase() && now - previous.at < 120000) {
        trackEvent('search_reformulated', {
          fromQueryLength: previous.query.length,
          toQueryLength: trimmedQuery.length,
          searchType,
        });
      }

      lastSearchRef.current = { query: trimmedQuery, at: now };
      trackEvent('search_submitted', {
        query: trimmedQuery.slice(0, 120),
        queryLength: trimmedQuery.length,
        searchType,
        source,
      });
    }

    await performSearch(trimmedQuery, offset);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    await submitSearch(searchQuery, 0, 'manual');
  };

  const handleLoadMore = () => {
    const nextOffset = pagination.offset + 30;
    pendingResultScrollRef.current = true;
    submitSearch(searchQuery, nextOffset, 'pagination');
  };

  const handlePrevious = () => {
    const newOffset = Math.max(0, pagination.offset - 30);
    pendingResultScrollRef.current = true;
    submitSearch(searchQuery, newOffset, 'pagination');
  };

  const handleClear = () => {
    searchRequestSeqRef.current += 1;
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    setLoading(false);
    setResults([]);
    setPagination({});
    setSearchQuery('');
    setError(null);
    setSuggestions([]);
    setPreviewResults([]);
    setDidYouMean('');
    setAutoCorrection(null);
    setFacets({ availability: {}, itemTypes: {}, years: {} });
    setFacetFilters({ availability: 'all', itemType: 'all', year: 'all' });
    suggestionsCacheRef.current.clear();
    previewCacheRef.current.clear();
  };

  const renderHighlightedText = (text) => {
    const value = String(text || '');
    if (!value || !highlightedTokens.length) {
      return value;
    }

    const pattern = highlightedTokens.map((token) => escapeRegExp(token)).join('|');
    const regex = new RegExp(`(${pattern})`, 'ig');
    const parts = value.split(regex);

    return parts.map((part, index) => {
      const lowerPart = part.toLowerCase();
      if (highlightedTokens.includes(lowerPart)) {
        return <mark className="result-mark" key={`h-${index}`}>{part}</mark>;
      }
      return <span key={`t-${index}`}>{part}</span>;
    });
  };

  const runSuggestedSearch = (queryText, source = 'suggestion') => {
    setSearchQuery(queryText);
    setShowSuggestions(false);
    setSuggestions([]);
    setPreviewResults([]);
    if (source === 'suggestion') {
      trackEvent('search_suggestion_clicked', {
        query: queryText.slice(0, 120),
        searchType,
      });
    }
    submitSearch(queryText, 0, source);
  };

  const handleViewDetails = (book) => {
    trackEvent('book_detail_opened', {
      from: 'home-search',
      biblioId: book.biblio_id,
    });

    try {
      localStorage.setItem('searchState', JSON.stringify({
        query: searchQuery,
        type: searchType,
        results,
        pagination,
      }));
      localStorage.setItem('searchScrollPosition', window.scrollY.toString());
    } catch (_error) {
      // Ignore storage quota or private mode errors.
    }
    navigate(`/book/${book.biblio_id}`, { state: { from: 'home-search' } });
  };

  const handleResultOpen = (book, index) => {
    trackEvent('search_result_clicked', {
      searchType,
      query: searchQuery.trim().slice(0, 120),
      rank: (pagination.offset || 0) + index + 1,
      biblioId: book.biblio_id,
    });
    handleViewDetails(book);
  };

  const toggleReadingList = (book) => {
    if (isInReadingList(book.biblio_id)) {
      setReadingList(removeFromReadingList(book.biblio_id));
      return;
    }

    setReadingList(addToReadingList(book));
  };

  const handleExportRis = () => {
    if (!readingList.length) {
      return;
    }
    const content = generateRis(readingList);
    downloadTextFile('opac-reading-list.ris', content, 'application/x-research-info-systems');
  };

  const handleExportBibtex = () => {
    if (!readingList.length) {
      return;
    }
    const content = generateBibtex(readingList);
    downloadTextFile('opac-reading-list.bib', content, 'application/x-bibtex');
  };

  const handleAdvancedSearchClick = () => {
    trackEvent('advanced_search_opened', { from: 'home-search' });
    navigate('/advanced-search');
  };

  const handlePreviewSelect = (book) => {
    trackEvent('search_preview_clicked', {
      searchType,
      query: searchQuery.trim().slice(0, 120),
      biblioId: book.biblio_id,
    });
    setShowSuggestions(false);
    handleViewDetails(book);
  };

  const handleSearchInputKeyDown = (e) => {
    if (!isDropdownOpen || navigableOptions.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveOptionIndex((prev) => (prev + 1) % navigableOptions.length);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveOptionIndex((prev) => (prev <= 0 ? navigableOptions.length - 1 : prev - 1));
      return;
    }

    if (e.key === 'Enter' && activeOptionIndex >= 0) {
      e.preventDefault();
      const option = navigableOptions[activeOptionIndex];
      if (option.type === 'preview') {
        handlePreviewSelect(option.book);
      } else {
        runSuggestedSearch(option.suggestion, 'suggestion');
      }
      return;
    }

    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <>
      <section className="search-section">
        <h2>Search OPAC</h2>
        <p className="search-hero-sub">Search across books, journals, e-resources and more</p>
        <div className="search-box">
          <form onSubmit={handleSearch}>
            <div className="search-input-group">
              <div className="search-input-wrap" ref={inputWrapRef}>
                <input
                  type="text"
                  id="searchInput"
                  value={searchQuery}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onFocus={() => setShowSuggestions(Boolean(searchQuery.trim()))}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
                  onKeyDown={handleSearchInputKeyDown}
                  placeholder="Search for books, journals, articles..."
                  className="search-input"
                  autoComplete="off"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={isDropdownOpen}
                  aria-controls="search-suggestions-listbox"
                  aria-activedescendant={activeOptionIndex >= 0 ? `search-option-${activeOptionIndex}` : undefined}
                  aria-label="Search for books, journals, articles"
                />
                {isDropdownOpen && dropdownRect && createPortal(
                  <div
                    id="search-suggestions-listbox"
                    className="search-suggestions search-suggestions--portal"
                    role="listbox"
                    aria-label="Search suggestions"
                    style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
                  >
                    {previewResults.length > 0 && (
                      <div className="search-preview-list">
                        {previewResults.map((book, idx) => (
                          <button
                            key={book.biblio_id}
                            id={`search-option-${idx}`}
                            type="button"
                            role="option"
                            aria-selected={activeOptionIndex === idx}
                            className={`search-preview-item${activeOptionIndex === idx ? ' search-preview-item--active' : ''}`}
                            onMouseDown={() => handlePreviewSelect(book)}
                            onMouseEnter={() => setActiveOptionIndex(idx)}
                          >
                            <CoverImage
                              biblioId={book.biblio_id}
                              isbn={book.isbn}
                              alt=""
                              className="search-preview-item__cover"
                              width={36}
                              height={52}
                              sizeCode="S"
                            />
                            <span className="search-preview-item__info">
                              <span className="search-preview-item__title">{book.title}</span>
                              {book.author && <span className="search-preview-item__author">{book.author}</span>}
                              <span className="search-preview-item__avail">
                                {Number(book.available_count || 0) > 0
                                  ? `${book.available_count} available`
                                  : 'Unavailable'}
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {suggestions.length > 0 && (
                      <div className="search-suggestion-list">
                        {suggestions.map((suggestion, sIdx) => {
                          const idx = previewResults.length + sIdx;
                          return (
                            <button
                              key={suggestion}
                              id={`search-option-${idx}`}
                              type="button"
                              role="option"
                              aria-selected={activeOptionIndex === idx}
                              className={`search-suggestion-item${activeOptionIndex === idx ? ' search-suggestion-item--active' : ''}`}
                              onMouseDown={() => runSuggestedSearch(suggestion, 'suggestion')}
                              onMouseEnter={() => setActiveOptionIndex(idx)}
                            >
                              {suggestion}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>,
                  document.body
                )}
              </div>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="search-type"
                aria-label="Search type"
              >
                {searchTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <button type="submit" className="search-btn" disabled={loading}>
                <span className="search-icon" aria-hidden="true">🔍</span>
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>

            <div
              className="search-pills-row"
              aria-label="Recent and popular search suggestions"
              style={isDropdownOpen ? { visibility: 'hidden' } : undefined}
            >
              {recentSearches.slice(0, 4).map((item) => (
                <button
                  type="button"
                  key={`recent-${item.type}-${item.query}`}
                  className="search-pill recent"
                  onClick={() => runSuggestedSearch(item.query, 'recent')}
                >
                  Recent: {item.query}
                </button>
              ))}
              {popularSearches.slice(0, 4).map((item) => (
                <button
                  type="button"
                  key={`popular-${item.query}`}
                  className="search-pill popular"
                  onClick={() => runSuggestedSearch(item.query, 'popular')}
                >
                  Popular: {item.query}
                </button>
              ))}
            </div>

            <div className="advanced-search-link">
              <button type="button" className="advanced-search-btn" onClick={handleAdvancedSearchClick}>
                Advanced Search
              </button>
            </div>
          </form>
        </div>

        {error && !autoCorrection && <div className="error-message" role="alert">{error}</div>}

        {autoCorrection && (
          <div className="did-you-mean did-you-mean--auto" role="status" aria-live="polite">
            Showing results for <strong>{autoCorrection.to}</strong> instead of "{autoCorrection.from}".{' '}
            <button type="button" onClick={() => runSuggestedSearch(autoCorrection.from, 'auto-correct-revert')}>
              Search instead for "{autoCorrection.from}"
            </button>
          </div>
        )}

        {!autoCorrection && didYouMean && (
          <div className="did-you-mean" role="status" aria-live="polite">
            Did you mean{' '}
            <button type="button" onClick={() => runSuggestedSearch(didYouMean, 'did-you-mean')}>
              {didYouMean}
            </button>
            ?
          </div>
        )}

        {results.length > 0 && (
          <div className="search-results" ref={resultsRef}>
            <div className="results-header">
              <h3 role="status" aria-live="polite">Results ({pagination.total || results.length})</h3>
              <div className="results-header__actions">
                <div className="view-mode-toggle" role="group" aria-label="Results view">
                  <button
                    type="button"
                    className={`view-mode-btn${viewMode === 'list' ? ' view-mode-btn--active' : ''}`}
                    aria-pressed={viewMode === 'list'}
                    onClick={() => setViewMode('list')}
                  >
                    ☰ List
                  </button>
                  <button
                    type="button"
                    className={`view-mode-btn${viewMode === 'mosaic' ? ' view-mode-btn--active' : ''}`}
                    aria-pressed={viewMode === 'mosaic'}
                    onClick={() => setViewMode('mosaic')}
                  >
                    ▦ Mosaic
                  </button>
                </div>
                <button className="clear-btn" onClick={handleClear}>✕ Clear</button>
              </div>
            </div>

            <div className="reading-list-toolbar">
              <span>Reading List: {readingList.length}</span>
              <div className="reading-list-actions">
                <button type="button" className="reading-export-btn" onClick={handleExportRis} disabled={!readingList.length}>
                  Export RIS
                </button>
                <button type="button" className="reading-export-btn" onClick={handleExportBibtex} disabled={!readingList.length}>
                  Export BibTeX
                </button>
                <button type="button" className="reading-clear-btn" onClick={() => setReadingList(clearReadingList())} disabled={!readingList.length}>
                  Clear List
                </button>
              </div>
            </div>

            <div className="result-facets">
              <label>
                Availability
                <select
                  value={facetFilters.availability}
                  onChange={(e) => setFacetFilters((prev) => ({ ...prev, availability: e.target.value }))}
                >
                  <option value="all">
                    All ({(liveFacets.availability.available || 0) + (liveFacets.availability.unavailable || 0)})
                  </option>
                  <option value="available">Available ({liveFacets.availability.available || 0})</option>
                  <option value="unavailable">Unavailable ({liveFacets.availability.unavailable || 0})</option>
                </select>
              </label>

              <label>
                Item Type
                <select
                  value={facetFilters.itemType}
                  onChange={(e) => setFacetFilters((prev) => ({ ...prev, itemType: e.target.value }))}
                >
                  <option value="all">All types ({filteredResults.length})</option>
                  {Object.entries(liveFacets.itemTypes)
                    .sort((a, b) => b[1] - a[1])
                    .map(([type, count]) => (
                      <option key={type} value={type}>{itemTypeLabel(type)} ({count})</option>
                    ))}
                </select>
              </label>

              <label>
                Year
                <select
                  value={facetFilters.year}
                  onChange={(e) => setFacetFilters((prev) => ({ ...prev, year: e.target.value }))}
                >
                  <option value="all">All years ({filteredResults.length})</option>
                  {Object.entries(liveFacets.years)
                    .sort((a, b) => Number(b[0]) - Number(a[0]))
                    .map(([year, count]) => (
                      <option key={year} value={year}>{year} ({count})</option>
                    ))}
                </select>
              </label>
            </div>

            {pagination.currentPage && (
              <div className="pagination-info-top">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
            )}

            {viewMode === 'mosaic' ? (
              <div className="results-mosaic">
                {filteredResults.map((book, index) => (
                  <button
                    key={book.biblio_id}
                    type="button"
                    className="mosaic-item"
                    ref={index === 0 ? firstResultRef : undefined}
                    onClick={() => handleResultOpen(book, index)}
                    title={`${book.title}${book.author ? ` by ${book.author}` : ''}`}
                  >
                    <CoverImage
                      biblioId={book.biblio_id}
                      isbn={book.isbn}
                      alt={book.title}
                      className="mosaic-item__cover"
                      width={120}
                      height={170}
                      sizeCode="M"
                    />
                    <span className="mosaic-item__title">{book.title}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="results-list">
                {filteredResults.map((book, index) => (
                  <div
                    key={book.biblio_id}
                    className="result-item"
                    ref={index === 0 ? firstResultRef : undefined}
                  >
                    <div className="result-cover-wrap">
                      <CoverImage
                        biblioId={book.biblio_id}
                        isbn={book.isbn}
                        alt={book.title}
                        className="result-cover"
                        width={96}
                        height={140}
                        sizeCode="M"
                      />
                    </div>
                    <div className="result-content">
                      <h4>{renderHighlightedText(book.title)}</h4>
                      {book.author && <p className="author">by {renderHighlightedText(book.author)}</p>}
                      {book.isbn && <p className="isbn">ISBN: {book.isbn}</p>}
                      <p className="item-count">📚 {book.item_count} item(s), {Number(book.available_count || 0)} available</p>
                      <button
                        onClick={() => handleResultOpen(book, index)}
                        className="details-btn"
                      >
                        View Details
                      </button>
                      <button
                        type="button"
                        className="reading-toggle-btn"
                        onClick={() => toggleReadingList(book)}
                      >
                        {isInReadingList(book.biblio_id) ? 'Remove from Reading List' : 'Add to Reading List'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="pagination-container">
              {pagination.canGoBack && (
                <button
                  className="pagination-btn prev-btn"
                  onClick={handlePrevious}
                  disabled={loading}
                >
                  {loading ? '⏳' : '← Previous'}
                </button>
              )}
              {pagination.currentPage && (
                <span className="pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
              )}
              {pagination.hasMore && (
                <button
                  className="pagination-btn next-btn"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? '⏳' : 'Next →'}
                </button>
              )}
            </div>
          </div>
        )}

        {loading && (
          <div className="loading">
            <span>🔍 Searching our catalog...</span>
          </div>
        )}
      </section>
    </>
  );
}

export default SearchSection;
